import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { openFromUrlInNewTab } from '@clatter/platform';
import { addNotification } from '../store';
import { useLazyGetAssetPresignedUrlQuery, useLazyGetAssetVersionPresignedUrlQuery } from '../services';

export function useAssetDownload() {
  const dispatch = useDispatch();

  const [triggerGetAssetPresignedUrl, { data: assetPresignedUrlData, error: assetPresignedUrlError, isFetching }] =
    useLazyGetAssetPresignedUrlQuery();

  const [triggerGetAssetVersionPresignedUrl, { data: assetVersionPresignedUrlData, error: assetVersionPresignedUrlError, isFetching: isAssetVersionFetching }] =
    useLazyGetAssetVersionPresignedUrlQuery();

  const downloadAssetById = ({ assetId }: { assetId: number }) => {
    if (!assetId) {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Could not download file: No asset ID provided!',
        }),
      );
      return;
    }

    triggerGetAssetPresignedUrl({ assetId });
  };

  const downloadAssetVersionById = ({ assetId, assetVersionId, }: { assetId: number, assetVersionId: number }) => {
    if (!assetId) {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Could not download file: No asset ID provided!',
        }),
      );
      return;
    }

    if (!assetVersionId) {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Could not download file: No asset version ID provided!',
        }),
      );
      return;
    }

    triggerGetAssetVersionPresignedUrl({ assetId: assetId, versionId: assetVersionId });
  };

  useEffect(() => {
    if (!assetPresignedUrlData) return;

    if (assetPresignedUrlData.data) {
      openFromUrlInNewTab(assetPresignedUrlData.data);
    } else {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Error: No presigned URL received for asset download!',
        }),
      );
    }
  }, [assetPresignedUrlData, dispatch]);

  useEffect(() => {
    if (!assetVersionPresignedUrlData) return;

    if (assetVersionPresignedUrlData.data) {
      openFromUrlInNewTab(assetVersionPresignedUrlData.data);
    } else {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Error: No presigned URL received for asset version download!',
        }),
      );
    }
  }, [assetVersionPresignedUrlData, dispatch]);

  useEffect(() => {
    if (assetPresignedUrlError) {
      console.error('Error fetching presigned URL:', assetPresignedUrlError);
      dispatch(
        addNotification({
          type: 'error',
          message: 'Error downloading asset: Failed to fetch presigned URL.',
        }),
      );
    }
  }, [assetPresignedUrlError, dispatch]);

  useEffect(() => {
    if (assetPresignedUrlError) {
      console.error('Error fetching presigned asset version URL:', assetVersionPresignedUrlError);
      dispatch(
        addNotification({
          type: 'error',
          message: 'Error downloading asset version: Failed to fetch presigned URL.',
        }),
      );
    }
  }, [assetVersionPresignedUrlError, dispatch]);

  return {
    downloadAssetById,
    downloadAssetVersionById,
    isFetching: isFetching || isAssetVersionFetching,
  };
}
